import { useState, useEffect } from 'react';
import { useDDPCall } from '@zedoc/ddp-connector';

/**
 * Custom hook for managing iframe embed token generation and URL construction
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.apiSpec - The API spec to call for token generation
 * @param {Object} config.params - Parameters to pass to the API call
 *   Can include: recipientId, projectId, participationId, etc.
 *   Pass only what's needed for the specific endpoint
 *
 * @returns {Object} Hook state
 * @returns {string|null} embedUrl - The final embed URL with token and sessionId
 * @returns {boolean} loading - Loading state
 * @returns {string|null} error - Error message if any
 *
 * @example
 * // For Patient Bundle (only needs recipientId)
 * const { embedUrl, loading, error } = useIframeEmbed({
 *   apiSpec: apiZedocGetPatientBundleToken,
 *   params: { recipientId: 'xyz123' },
 * });
 *
 * @example
 * // For Project Bundle (only needs projectId)
 * const { embedUrl, loading, error } = useIframeEmbed({
 *   apiSpec: apiZedocGetProjectBundleToken,
 *   params: { projectId: 'abc456' },
 * });
 *
 * @example
 * // For Patient-Project Bundle (needs projectId and participationId)
 * const { embedUrl, loading, error } = useIframeEmbed({
 *   apiSpec: apiZedocGetPatientProjectBundleToken,
 *   params: {
 *     projectId: 'abc456',
 *     participationId: 'def789',
 *   },
 * });
 *
 * @example
 * // For features that don't need parameters
 * const { embedUrl, loading, error } = useIframeEmbed({
 *   apiSpec: apiZedocGetMedicationConflictToken,
 *   params: {},
 * });
 */
const useIframeEmbed = ({ apiSpec, params = {} }) => {
  const { ddpCall } = useDDPCall();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Execute DDP call to get embed token
    ddpCall(apiSpec.withParams(params))
      .then(({ embedUrl: receivedEmbedUrl, sessionId }) => {
        // Don't append sessionId to URL - will be sent via postMessage instead
        // This prevents users from copying the URL with sessionId to incognito mode
        setEmbedUrl(receivedEmbedUrl);
        setSessionId(sessionId); // Store sessionId for postMessage
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load embed tool');
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiSpec, JSON.stringify(params), ddpCall]);

  return {
    embedUrl,
    sessionId, // NEW - for postMessage
    loading,
    error,
  };
};

export default useIframeEmbed;
